import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Flex, Text, SimpleGrid, Icon, HStack, VStack,
    Input, Button, useToast, Skeleton,
    Tag, TagLabel, Wrap, WrapItem, InputGroup, InputLeftElement
} from '@chakra-ui/react';
import { FaDollarSign, FaShoppingCart, FaChartLine, FaCalendarAlt, FaSearch, FaSyncAlt, FaCoins, FaInbox, FaHandshake } from 'react-icons/fa';
import CardCanalComissao from './CardCanalComissao';
import LinhaComissaoVenda from './LinhaComissaoVenda';
import { getResumoComissoesVendedor, sincronizarComissoesParceiros } from '../../services/api';
import { checkPermission } from '../../services/permission';

const formatarMoeda = (valor) => {
    const numero = Number(valor) || 0;
    return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const dataHoje = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dataInicioMes = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export const StatCard = ({ icon, label, value, currency = false, accent = '#F68633', loading, labelLines = 1 }) => (
    <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.200"
        borderTopWidth="3px"
        borderTopColor={accent}
        p={{ base: 4, md: 5 }}
        transition="all 0.2s"
        _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
    >
        <Flex align="center" gap={4}>
            <Flex
                bg={`${accent}15`}
                w={12}
                h={12}
                borderRadius="lg"
                align="center"
                justify="center"
                flexShrink={0}
            >
                <Icon as={icon} color={accent} boxSize={5} />
            </Flex>
            <VStack align="flex-start" spacing={0.5} flex="1" minW={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" noOfLines={labelLines}>
                    {label}
                </Text>
                {loading ? (
                    <Skeleton height="28px" width="100px" mt={1} />
                ) : (
                    <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="gray.800" lineHeight="short">
                        {currency && <Text as="span" fontSize="md" color="gray.500" mr={1}>R$</Text>}{value}
                    </Text>
                )}
            </VStack>
        </Flex>
    </Box>
);

export const SectionHeader = ({ children }) => (
    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3} px={1}>
        {children}
    </Text>
);

const FILTROS_CANAL = [
    { value: 'todos', label: 'Todos os canais', cor: 'gray' },
    { value: 'site', label: 'Vendas do Site', cor: 'orange' },
    { value: 'ml', label: 'Mercado Livre', cor: 'yellow' },
    { value: 'manual', label: 'Vendas Manuais', cor: 'green' },
];

const PainelComissoes = ({ userId }) => {
    const toast = useToast();
    const isAdm = checkPermission();
    const [dataInicio, setDataInicio] = useState(dataInicioMes());
    const [dataFim, setDataFim] = useState(dataHoje());
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sincronizando, setSincronizando] = useState(false);
    const [filtroCanal, setFiltroCanal] = useState('todos');
    const [busca, setBusca] = useState('');

    const carregar = async () => {
        if (!userId) return;
        setLoading(true);
        const res = await getResumoComissoesVendedor(userId, dataInicio, dataFim);
        setLoading(false);
        if (res) {
            setDados(res);
        } else {
            toast({ title: 'Erro ao carregar comissões', status: 'error', isClosable: true });
        }
    };

    const handleSincronizarParceiros = async () => {
        setSincronizando(true);
        const res = await sincronizarComissoesParceiros({ dias: 60 });
        setSincronizando(false);
        if (res && res.success) {
            const r = res.resumo || {};
            toast({
                title: 'Sincronização concluída',
                description: `Consultados: ${r.consultados || 0} · Vendas atualizadas: ${r.vendas_atualizadas || 0} · Comissões criadas: ${r.comissoes_criadas || 0}`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            carregar();
        } else {
            toast({ title: 'Falha na sincronização com Sistema de Parceiros', status: 'error', isClosable: true });
        }
    };

    useEffect(() => {
        carregar();
    }, [userId]);

    const handleAtualizarRegistroVenda = (registroAtualizado) => {
        if (!dados) return;
        const novasVendas = dados.vendas.map((v) =>
            v.origem_id === registroAtualizado.origem_id ? { ...v, ...registroAtualizado } : v
        );
        setDados({ ...dados, vendas: novasVendas });
        carregar();
    };

    const vendasFiltradas = useMemo(() => {
        if (!dados?.vendas) return [];
        const termo = busca.trim().toLowerCase();
        return dados.vendas.filter((v) => {
            if (filtroCanal !== 'todos' && v.canal !== filtroCanal) return false;
            if (!termo) return true;
            const alvo = `${v.origem_id} ${v.id_origem || ''} ${v.cliente || ''} ${v.pack_id || ''} ${v.cupom || ''} ${v.partner_referral_code || ''}`.toLowerCase();
            return alvo.includes(termo);
        });
    }, [dados, filtroCanal, busca]);

    const totais = dados?.totais || { volume: 0, valor_base: 0, valor_comissao: 0, ticket_medio: 0 };
    const canais = dados?.canais || [];

    return (
        <Box maxW={{ base: '100%', md: '92%', lg: '85%' }} mx="auto" px={{ base: 3, md: 6 }}>
            <Flex
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                p={{ base: 3, md: 4 }}
                mb={4}
                gap={3}
                align={{ base: 'stretch', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                flexWrap="wrap"
            >
                <HStack spacing={2} flex="1">
                    <Icon as={FaCalendarAlt} color="gray.500" />
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        Período da análise
                    </Text>
                </HStack>
                <HStack spacing={2} flexWrap="wrap">
                    <Input
                        type="date"
                        size="sm"
                        borderRadius="md"
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                        maxW="160px"
                    />
                    <Text fontSize="xs" color="gray.500">até</Text>
                    <Input
                        type="date"
                        size="sm"
                        borderRadius="md"
                        value={dataFim}
                        onChange={(e) => setDataFim(e.target.value)}
                        maxW="160px"
                    />
                    <Button
                        size="sm"
                        leftIcon={<Icon as={FaSyncAlt} />}
                        bg="#F68633"
                        color="white"
                        _hover={{ bg: '#E07520' }}
                        onClick={carregar}
                        isLoading={loading}
                    >
                        Atualizar
                    </Button>
                    {isAdm && (
                        <Button
                            size="sm"
                            leftIcon={<Icon as={FaHandshake} />}
                            variant="outline"
                            colorScheme="purple"
                            onClick={handleSincronizarParceiros}
                            isLoading={sincronizando}
                            loadingText="Sincronizando"
                        >
                            Sincronizar Parceiros
                        </Button>
                    )}
                </HStack>
            </Flex>

            <SectionHeader>Resumo de comissões</SectionHeader>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={5}>
                <StatCard
                    icon={FaCoins}
                    label="Comissão Total"
                    value={formatarMoeda(totais.valor_comissao)}
                    currency
                    accent="#38A169"
                    loading={loading}
                />
                <StatCard
                    icon={FaShoppingCart}
                    label="Volume de Vendas"
                    value={totais.volume}
                    accent="#3182CE"
                    loading={loading}
                />
                <StatCard
                    icon={FaDollarSign}
                    label="Valor Base Total"
                    value={formatarMoeda(totais.valor_base)}
                    currency
                    accent="#F68633"
                    loading={loading}
                />
                <StatCard
                    icon={FaChartLine}
                    label="Ticket Médio"
                    value={formatarMoeda(totais.ticket_medio)}
                    currency
                    accent="#805AD5"
                    loading={loading}
                />
            </SimpleGrid>

            <SectionHeader>Performance por canal</SectionHeader>
            {loading ? (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={5}>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} height="140px" borderRadius="xl" />
                    ))}
                </SimpleGrid>
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={5}>
                    {canais.map((c) => (
                        <CardCanalComissao
                            key={c.canal}
                            canal={c.canal}
                            volume={c.volume}
                            valorBase={c.valor_base}
                            valorComissao={c.valor_comissao}
                        />
                    ))}
                </SimpleGrid>
            )}

            <SectionHeader>Vendas com comissão ({dados?.vendas?.length || 0})</SectionHeader>
            <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" boxShadow="sm" p={{ base: 3, md: 5 }}>
                <Flex
                    direction={{ base: 'column', md: 'row' }}
                    gap={3}
                    align={{ base: 'stretch', md: 'center' }}
                    mb={4}
                    flexWrap="wrap"
                >
                    <InputGroup size="sm" maxW={{ base: '100%', md: '320px' }}>
                        <InputLeftElement pointerEvents="none">
                            <Icon as={FaSearch} color="gray.400" />
                        </InputLeftElement>
                        <Input
                            borderRadius="md"
                            placeholder="Buscar por ID, cliente, anúncio, cupom, ID parceiro..."
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                        />
                    </InputGroup>

                    <Wrap spacing={2}>
                        {FILTROS_CANAL.map((f) => (
                            <WrapItem key={f.value}>
                                <Tag
                                    size="md"
                                    variant={filtroCanal === f.value ? 'solid' : 'outline'}
                                    colorScheme={f.cor}
                                    cursor="pointer"
                                    onClick={() => setFiltroCanal(f.value)}
                                    px={3}
                                    py={1.5}
                                    borderRadius="full"
                                    fontWeight="semibold"
                                >
                                    <TagLabel>{f.label}</TagLabel>
                                </Tag>
                            </WrapItem>
                        ))}
                    </Wrap>
                </Flex>

                {loading ? (
                    <VStack spacing={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height="200px" width="100%" borderRadius="xl" />
                        ))}
                    </VStack>
                ) : vendasFiltradas.length === 0 ? (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        py={12}
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderStyle="dashed"
                        borderRadius="xl"
                    >
                        <Icon as={FaInbox} boxSize={10} color="gray.300" mb={2} />
                        <Text fontSize="md" fontWeight="semibold" color="gray.600">
                            Nenhuma venda encontrada no período.
                        </Text>
                    </Flex>
                ) : (
                    <VStack spacing={3} align="stretch">
                        {vendasFiltradas.map((v) => (
                            <LinhaComissaoVenda
                                key={`venda-${v.origem_id}`}
                                registro={v}
                                userId={userId}
                                onSalvo={handleAtualizarRegistroVenda}
                            />
                        ))}
                    </VStack>
                )}
            </Box>
        </Box>
    );
};

export default PainelComissoes;
